import React from 'react';
import i18n from 'i18next';
import { Button, Col, Modal, Row, Spin, Tooltip, message } from 'antd';
import { ArrowLeftOutlined, CloudSyncOutlined, FileZipOutlined,
  CreditCardOutlined, EditOutlined, FileAddOutlined } from '@ant-design/icons';
import { Group, Panel, Separator, useDefaultLayout } from "react-resizable-panels";
import OntologyNavigator from '../../components/owl/OntologyNavigator';
import OntologyEntityEditor from '../../components/owl/OntologyEntityEditor';
import SelectProperty from '../../components/widgets/SelectProperty';

const getRouteIRI = (router) => {
  if (!router) return undefined;
  if (router.params && router.params.id) return decodeURIComponent(router.params.id);

  const search = router.location && router.location.search ? router.location.search : '';
  if (search) {
    const params = new URLSearchParams(search);
    const iri = params.get('iri') || params.get('uri');
    if (iri) return decodeURIComponent(iri);
  }

  const hash = router.location && router.location.hash ? router.location.hash : '';
  if (hash && hash.length > 1) return decodeURIComponent(hash.substring(1));

  return undefined;
};

const normalizeTTL = (content) => {
  if (typeof content !== 'string') return '';
  if (content.startsWith('"') && content.endsWith('"')) {
    try {
      return JSON.parse(content);
    } catch (e) {
      return content;
    }
  }
  return content;
};

const saveTextFile = (content, fileName, type = 'text/turtle;charset=utf-8') => {
  const blob = new Blob([content], {type});
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

class SKSAOntology extends React.Component {
  state = {
    operation: "view",
    ready: false,
    navigatorWidth: 34,
    resizing: false,
    adding: false,
    addedPredicates: [],
  };

  layoutRef = React.createRef();
  formRef = React.createRef();

  componentWillUnmount() {
    this.stopResize();
  }

  componentDidMount() {
    this.loadTreesAndPosition();
  }

  componentDidUpdate(prevProps) {
    const iri = getRouteIRI(this.props.router);
    const prevIri = getRouteIRI(prevProps.router);
    if (iri && iri !== prevIri) {
      this.setState({adding: false, addedPredicates: []});
      this.selectElement(iri);
    }
  }

  loadTreesAndPosition() {
    const { onLoadTrees } = this.props;
    const iri = getRouteIRI(this.props.router) || this.props.selectedTreeElement;
    this.setState({ready: false});
    const loader = onLoadTrees ? onLoadTrees() : Promise.resolve();
    return loader.then(() => {
      if (iri) this.selectElement(iri);
      this.setState({ready: true});
    }).catch(error => {
      this.setState({ready: true});
      return error;
    });
  }

  selectElement(iri) {
    if (!iri || !this.props.onSelectElement) return;
    this.props.onSelectElement(iri, this.props.router);
  }

  onRefresh = () => {
    return this.loadTreesAndPosition();
  };

  startResize = (event) => {
    event.preventDefault();
    this.setState({resizing: true});
    window.addEventListener('mousemove', this.onResize);
    window.addEventListener('mouseup', this.stopResize);
  };

  onResize = (event) => {
    if (!this.layoutRef.current) return;
    const rect = this.layoutRef.current.getBoundingClientRect();
    const width = ((event.clientX - rect.left) / rect.width) * 100;
    const navigatorWidth = Math.max(22, Math.min(55, width));
    this.setState({navigatorWidth});
  };

  stopResize = () => {
    window.removeEventListener('mousemove', this.onResize);
    window.removeEventListener('mouseup', this.stopResize);
    if (this.state && this.state.resizing)
      this.setState({resizing: false});
  };

  onSelectOperation(e, operation) { 
    e.preventDefault();
    if (operation === "export"){
      const { onExportOntology } = this.props;
      if (!onExportOntology) return;
      this.setState({exporting: true});
      onExportOntology()
        .then(response => {
          const content = normalizeTTL(response);
          if (!content) {
            message.warning("Нет данных для экспорта");
            return;
          }
          saveTextFile(content, "sksa-ontology.ttl");
          message.success("Онтология сохранена в TTL");
        })
        .catch(error => {
          message.error(`Ошибка экспорта: ${error}`);
        })
        .finally(() => this.setState({exporting: false}));
      return;
    };

    this.setState({operation, adding: false});
  }

  submitAddProperty = () => {
    const form = this.formRef.current;
    if (!form) return;
    form.validateFields().then(({predicateIri}) => {
      this.setState(state => ({
        adding: false,
        addedPredicates: state.addedPredicates.includes(predicateIri) ? state.addedPredicates : [...state.addedPredicates, predicateIri],
      }));
    }).catch(() => {});
  };

  render() {
    const {
      classTree,
      propertyTree,
      classDetails,
      propertyDetails,
      treeElementDetails,
      selectedTreeElement,
      load,
      lang,
      router,
      vselected,
      onSaveEntity,
      onReloadEntity,
      factory,
    } = this.props;
    const { operation, adding, addedPredicates } = this.state;

    const selectedIRI = getRouteIRI(router) || selectedTreeElement;
    const entity = selectedIRI ? (classDetails[selectedIRI] || propertyDetails[selectedIRI]) : undefined;
    const details = selectedIRI && treeElementDetails ? treeElementDetails[selectedIRI] : undefined;
    const existingPredicates = details && Array.isArray(details.outgoing)
      ? details.outgoing.map(item => item.predicat && item.predicat.value).filter(Boolean) : [];

    return (
      <>
        <Row><h1>{i18n.t('views.admin.landing.sksaowl')}</h1></Row>
        <Row>
          <Col className="csi_filters" span={6}>
            <Tooltip placement="top" title={i18n.t('views.adms.return_catalog')}>
              <Button icon={<ArrowLeftOutlined />} onClick={() => router && router.navigate('/')} />
            </Tooltip>
            &nbsp;&nbsp;
            <Tooltip placement="top" title={i18n.t('views.adms.list_reload_title')}>
              <Button icon={<CloudSyncOutlined />} onClick={this.onRefresh} />
            </Tooltip>
          </Col>
          <Col className="csi_asset_list" span={18} style={{"display": "flex", "flexDirection": "row-reverse"}}>
            {operation!="view" && (
              <>
                <Tooltip placement="top" title={"Режим просмотра данных"}>
                  <Button type="primary" icon={<CreditCardOutlined />} onClick={(e)=> this.onSelectOperation(e, "view")} />
                </Tooltip>
              </>
            )}
            {operation != "edit" && (
              <Tooltip placement="top" title={"Режим редактирования"}>
                  <Button icon={<EditOutlined />} onClick={(e)=> this.onSelectOperation(e, "edit")}/>
              </Tooltip>
            )}
            {operation === "edit" && selectedIRI && (
              <Tooltip placement="top" title="Добавить свойство">
                <Button icon={<FileAddOutlined />} onClick={() => this.setState({adding: true})}/>
              </Tooltip>
            )}
            &nbsp;&nbsp;
            <Tooltip placement="top" title="Экспорт в TTL">
              <Button
                icon={<FileZipOutlined />}
                loading={this.state.exporting}
                onClick={(e)=> this.onSelectOperation(e, "export")}
              />
            </Tooltip>
          </Col>
        </Row>
        <div
          ref={this.layoutRef}
          style={{
            display: 'flex',
            gap: 0,
            marginTop: 12,
            minHeight: 500,
            cursor: this.state.resizing ? 'col-resize' : undefined,
            userSelect: this.state.resizing ? 'none' : undefined,
          }}
        >
          <div style={{width: `${this.state.navigatorWidth}%`, paddingRight: 8, minWidth: 260}}>
            {!this.state.ready ? (
              <Spin />
            ) : (
              <OntologyNavigator
                classTree={classTree}
                propertyTree={propertyTree}
                selectedIRI={selectedIRI}
                lang={lang}
                onSelect={iri => this.selectElement(iri)}
              />
            )}
          </div>
          <div
            onMouseDown={this.startResize}
            style={{
              width: 8,
              cursor: 'col-resize',
              flex: '0 0 8px',
              background: this.state.resizing ? '#91caff' : '#f0f0f0',
              borderLeft: '1px solid #d9d9d9',
              borderRight: '1px solid #d9d9d9',
            }}
          />
          <div style={{flex: 1, paddingLeft: 8, minWidth: 360}}>
            <OntologyEntityEditor
              iri={selectedIRI}
              entity={entity}
              details={details}
              lang={lang}
              factory={factory}
              propertyDetails={propertyDetails}
              vselected={vselected}
              loading={load && selectedIRI && !details}
              onSave={onSaveEntity}
              onReload={onReloadEntity}
              operation={operation}
              addedPredicates={addedPredicates}
            />
          </div>
        </div>
        <Modal style={{minWidth: "80%"}} open={adding} title="Добавление свойства"
          cancelText="Отмена" okText="Ок" onCancel={() => this.setState({adding: false})}
          onOk={this.submitAddProperty} destroyOnHidden>
          <SelectProperty formRef={this.formRef} subjectIri={selectedIRI}
            existing={[...existingPredicates, ...addedPredicates]} factory={factory} lang={lang}/>
        </Modal>
      </>
    );
  }
}

export default SKSAOntology;
